import capitalize from 'lodash.capitalize';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer, TImporterState } from '@feathr/blackbox';
import type { TStatusTheme } from '@feathr/components';
import { inferPillTheme, StatusPillCell, Tooltip } from '@feathr/components';

import { getImportProgressMode } from './ImportProgressCell';

interface IProps {
  importer: Importer;
  isRevampEnabled: boolean;
}

export const importerStateThemes: Record<TImporterState, TStatusTheme> = {
  ['draft']: 'neutral',
  ['pending']: 'warning',
  ['processing']: 'info',
  ['complete']: 'success',
  ['failed']: 'error',
};

function ImporterStateChip({ importer, isRevampEnabled }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const state = importer.get('state');

  if (!isRevampEnabled || !importer.get('is_running')) {
    return (
      <StatusPillCell
        status={capitalize(state)}
        theme={inferPillTheme(state, importerStateThemes)}
        truncate={false}
      />
    );
  }

  const progressMode = getImportProgressMode({ state, progress: importer.get('progress') });
  // Placeholder copy pending Product/Design (Feathr/anhinga#8645, decision 23).
  const tooltip =
    progressMode.kind === 'inProgress'
      ? t(
          '{{percent}}% of rows read. This import is running. We will email you when it finishes. If it cannot finish, its status changes to Failed.',
          { percent: progressMode.percent },
        )
      : t(
          'This import is running. We will email you when it finishes. If it cannot finish, its status changes to Failed.',
        );

  return (
    <Tooltip focusable={true} title={tooltip}>
      <StatusPillCell status={t('In progress')} theme={'warning'} truncate={false} />
    </Tooltip>
  );
}

export default observer(ImporterStateChip);
